import React, { useEffect, useRef } from 'react';

import { useResponsive } from '@/hooks/useResponsive';
import { isMobileByUA } from '@/utils/ua';

import { StarIcon } from '../../Icons/StarIcon';
import styles from './index.module.css';
// 导入真实图片
import portalContextStep1 from './portal_context_image_step1_cn.png';
import portalContextStep2 from './portal_context_image_step2_cn.png';
import portalContextStep3 from './portal_context_image_step3_cn.png';
import portalContextStep4 from './portal_context_image_step4_cn.png';

export interface ContextSectionProps {
  className?: string;
}

export const ContextSection: React.FC<ContextSectionProps> = ({ className }) => {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { isMobile } = useResponsive();

  const isMobileUa = isMobileByUA();

  useEffect(() => {
    const cards = cardRefs.current.filter(Boolean);
    const cleanupFunctions: (() => void)[] = [];

    const handleMouseMove = (card: HTMLDivElement, e: MouseEvent) => {
      if (isMobileUa) return;

      const highlight = card.querySelector(`.${styles.cardHighlight}`) as HTMLElement;
      if (!highlight) return;

      // 获取卡片的位置和尺寸
      const rect = card.getBoundingClientRect();
      // 计算鼠标在卡片内的相对位置
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // 设置高光位置跟随鼠标 - 青蓝色弥散效果
      highlight.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
      // 根据卡片背景调整透明度，青色弥散更适合深色背景
      highlight.style.opacity = '0.8'; // 进一步提高透明度让弥散效果更明显

      // 3D 旋转效果 - 加强角度，参考 3d-card.html
      const rotateX = (y - rect.height / 2) / 15; // 从 /20 改为 /15，加强效果
      const rotateY = (rect.width / 2 - x) / 15; // 从 /20 改为 /15，加强效果

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    };

    const handleMouseLeave = (card: HTMLDivElement) => {
      if (isMobileUa) return;
      const highlight = card.querySelector(`.${styles.cardHighlight}`) as HTMLElement;
      if (highlight) {
        highlight.style.opacity = '0';
      }
      // 添加更快的重置过渡
      card.style.transition = 'all 0.3s ease-out, transform 0.2s ease-out';
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0)';

      // 重置为原始过渡设置
      setTimeout(() => {
        card.style.transition = 'all 0.3s ease-out, transform 0.05s ease-out';
      }, 200);
    };

    // 为每个卡片添加事件监听器
    cards.forEach((card) => {
      const mouseMoveHandler = (e: MouseEvent) => handleMouseMove(card, e);
      const mouseLeaveHandler = () => handleMouseLeave(card);

      card.addEventListener('mousemove', mouseMoveHandler);
      card.addEventListener('mouseleave', mouseLeaveHandler);

      // 收集清理函数
      cleanupFunctions.push(() => {
        card.removeEventListener('mousemove', mouseMoveHandler);
        card.removeEventListener('mouseleave', mouseLeaveHandler);
      });
    });

    // 组件卸载时清理所有事件监听器
    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [isMobileUa]);

  const setCardRef = (index: number) => (el: HTMLDivElement | null) => {
    cardRefs.current[index] = el;
  };
  return (
    <section
      className={`${styles.contextSection} ${isMobile ? styles.mobileContextSection : ''} ${className || ''}`}
    >
      {/* Header */}
      <div className={`${styles.header} ${isMobile ? styles.mobileHeader : ''}`}>
        <h2 className={`${styles.title} ${isMobile ? styles.mobileTitle : ''}`}>重塑上下文</h2>
        <div className={`${styles.subtitle} ${isMobile ? styles.mobileSubtitle : ''}`}>
          <p className={`${styles.description} ${isMobile ? styles.mobileDescription : ''}`}>
            代码仓库的"望远镜"和"显微镜"，让开发真正化繁为简
          </p>
          <div className={`${styles.features} ${isMobile ? styles.mobileFeatures : ''}`}>
            <span className={styles.featureText}>敏锐</span>
            <StarIcon className={styles.starIcon} />
            <span className={styles.featureText}>务实</span>
            <StarIcon className={styles.starIcon} />
            <span className={styles.featureText}>明晰</span>
          </div>
        </div>
      </div>

      {/* Bento Grid */}
      <div className={`${styles.bentoGrid} ${isMobile ? styles.mobileBentoGrid : ''}`}>
        {/* Top Row */}
        <div className={`${styles.topRow} ${isMobile ? styles.mobileTopRow : ''}`}>
          {/* Full Codebase Context Awareness - Large Card */}
          <div
            className={`${styles.largeCard} ${styles.bentoCard} ${isMobile ? `${styles.mobileLargeCard} ${styles.mobileCard}` : ''}`}
            ref={setCardRef(0)}
          >
            <div className={styles.cardHighlight}></div>
            <div className={styles.cardImage}>
              <img
                src={portalContextStep1}
                alt="Full Codebase Context Awareness"
                className={styles.cardImageContent}
              />
            </div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>仓库级上下文实时感知</h3>
              <p className={styles.cardDescription}>
                深度理解并洞察企业级大型代码仓库，精准把握核心关键。
              </p>
            </div>
          </div>

          {/* Instantly Precise References - Small Card */}
          <div
            className={`${styles.smallCard} ${styles.bentoCard} ${isMobile ? `${styles.mobileSmallCard} ${styles.mobileCard}` : ''}`}
            ref={setCardRef(1)}
          >
            <div className={styles.cardHighlight}></div>
            <div className={styles.cardImage}>
              <img
                src={portalContextStep2}
                alt="Instantly Precise References"
                className={styles.cardImageContent}
              />
            </div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>精准引用，即刻可用</h3>
              <p className={styles.cardDescription}>使用 # 快速引用指定文件或文件夹，精确定位。</p>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className={`${styles.bottomRow} ${isMobile ? styles.mobileBottomRow : ''}`}>
          {/* Custom Rules, Your Way - Small Card */}
          <div
            className={`${styles.smallCard} ${styles.bentoCard} ${isMobile ? `${styles.mobileSmallCard} ${styles.mobileCard}` : ''}`}
            ref={setCardRef(2)}
          >
            <div className={styles.cardHighlight}></div>
            <div className={styles.cardImage}>
              <img
                src={portalContextStep3}
                alt="Custom Rules, Your Way"
                className={styles.cardImageContent}
              />
            </div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>自定义规则，灵活自如</h3>
              <p className={styles.cardDescription}>
                利用可复用的自定义规则灵活定义智能，从代码风格到开发实践都可自由设定。
              </p>
            </div>
          </div>

          {/* Visual-Ready - Large Card */}
          <div
            className={`${styles.largeCard} ${styles.bentoCard} ${isMobile ? `${styles.mobileLargeCard} ${styles.mobileCard}` : ''}`}
            ref={setCardRef(3)}
          >
            <div className={styles.cardHighlight}></div>
            <div className={styles.cardImage}>
              <img
                src={portalContextStep4}
                alt="Visual-Ready"
                className={styles.cardImageContent}
              />
            </div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>图像秒转代码，快速落地</h3>
              <p className={styles.cardDescription}>
                输入 UI 设计稿或异常错误截图，即刻生成代码或调试洞察。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
